import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import { RootState } from "../index";
import {
  destroyComment,
  fetchComment,
  fetchComments,
  fetchReplies,
  updateComment
} from "../../actions/mine_craft/commentsActions";
import { fetchReport, fetchReports } from "../../actions/mine_craft/reportsActions";

export interface CommentInterface {
  id: number;
  url: string;
  body: string;
  sender_id: EntityId;
  target_id: EntityId;
  target_type: string;
  parent_target_id: number;
  parent_target_type: string;
  likes_count: number;
  comments_count: number;
  created_at: string;
  updated_at: string;
  comments: EntityId[];
  hidden: boolean;
  rank?: number;
}

export interface initialInterface {
  error: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  count: number;

  editId: EntityId | null;
  editError: string | null;

  preloadReplies: any[];
}

const initialState: initialInterface = {
  error: null,
  status: 'idle',
  count: 0,

  editId: null,
  editError: null,

  preloadReplies: []
};

const commentsAdapter = createEntityAdapter<CommentInterface>();

const slice = createSlice({
  name: 'comments',
  initialState: commentsAdapter.getInitialState(initialState),
  reducers: {
    setStatus: (state, action) => {
      state.status = action.payload;
    },
    setEditId: (state, action) => {
      state.editId    = action.payload;
      state.editError = initialState.editError;
    },
    setEditError: (state, action) => {
      state.editError = action.payload;
    },
    clearReplies: (state, action) => {
      const comment = state.entities[action.payload];

      if(!comment) {
        return;
      }

      comment.comments = [];
      state.entities[action.payload] = comment;
    },
    setPreloadReplies: (state, action) => {
      state.preloadReplies = [action.payload, ...state.preloadReplies];
    },
    clearPreloadReplies: (state) => {
      state.preloadReplies = [];
    }
  },
  extraReducers: (builder) => {
    builder
      // #index
      .addCase(fetchComments.pending, (state, _action) => {
        state.status = 'loading'
      })
      .addCase(fetchComments.fulfilled, (state, action) => {
        state.count = action.payload.count;
        state.entities = action.payload.comments;
        state.ids = action.payload.ids;
        state.status = 'succeeded';
      })
      .addCase(fetchComments.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Unknown Error';
      })
      .addCase(fetchReplies.fulfilled, (state, action) => {
        if (!action.payload.ids.length) {
          return;
        }

        const id: EntityId = Object.values(action.payload.comments)[0].target_id;
        const comment = state.entities[id];

        if (!comment) {
          return;
        }

        comment.comments = [...comment.comments, ...action.payload.ids].sort((a, b) => (b-a));
        comment.comments_count = action.payload.count;

        state.entities[id] = comment;
        state.entities = {...state.entities, ...action.payload.comments};
      })

      // #show
      .addCase(fetchComment.fulfilled, (state, action) => {
        const comment     = Object.values(action.payload.comment)[0];
        const oldComment  = state.entities[comment.id];

        if (oldComment) {
          comment.comments = oldComment.comments;
        }

        state.entities[comment.id] = comment;
      })

      // #update
      .addCase(updateComment.fulfilled, (state, action) => {
        const comment     = Object.values(action.payload.comment)[0];
        const oldComment  = state.entities[comment.id];

        if (oldComment) {
          comment.comments = oldComment.comments;
        }

        state.entities[comment.id] = comment;
      })
      .addCase(updateComment.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.editError = error || action.error.message || 'Unknown Error';
      })

      // #destroy
      .addCase(destroyComment.fulfilled, (state, action) => {
        commentsAdapter.removeOne(state, action.payload.id);
      })

      // #reportComments
      .addCase(fetchReports.fulfilled, (state, action) => {
        state.entities = { ...state.entities, ...action.payload.comments };
      })
      .addCase(fetchReport.fulfilled, (state, action) => {
        state.entities = { ...state.entities, ...action.payload.comment };
      })
  }
});

const reducer = slice.reducer;
export default reducer;

export const {
  setEditId,
  setEditError,
  clearReplies,
  setPreloadReplies,
  clearPreloadReplies
} = slice.actions;

export const {
  selectById: selectCommentById,
  selectIds: selectCommentIds,
  selectEntities: selectCommentEntities
} = commentsAdapter.getSelectors<RootState>(state => state.mineCraft.comments);
